// The till's one road to the API: its order store, through the Bosla app's
// shell (#371, E5; #334 decisions 2, 3 and 10).
//
// The till runs inside the shell, which starts the order store on the
// till's own machine. The screen never calls the Bosla API: every till call
// goes to the store, which answers pairing, the sale, the day's orders,
// payment and void itself, keeps the till's identity, menu and areas, and
// forwards every other till call signed, with the cashier's session it holds
// (internal/orderstore). The shell relays each call with one command of its
// own, `store_http`, through Tauri's IPC, which the Bosla app's shell
// implements in Rust as a relay to the store on 127.0.0.1, adding the launch
// secret it gave the store, which the screen never sees (till-app/, #391,
// #392). The browser flows and the walkthroughs replace the shell with a test
// double that relays the same request to a real store (e2e/shell.ts).
//
// The shell answers three commands of its own besides (#392): the page
// reports ready with its own commit, asks to switch to a newer screen bundle
// at a safe moment, and reads what the app is. Its event store://status says
// whether the store is up. These are native level 1
// (till-app/testdata/compat/native-level), and only grow. Level 9 adds a
// fourth (#648): the lock screen asks to install a newer app update the store
// kept, at a safe moment (UC-57). Level 10 adds `turn_on_sharing` (#667),
// which a manager starts from Printers.
//
// In a plain browser there is no shell, so the till shows the notice that it
// runs in the Bosla app (D21) and calls nothing.

import { invoke, isTauri } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { BUNDLE_COMMIT, isCommit } from '../commit';

/** The command the shell answers with a call to the store. */
export const STORE_COMMAND = 'store_http';

/**
 * One call to the store, as the shell relays it.
 *
 * `path` is the till API's own path, such as `/api/till/orders`, or one of
 * the store's own under `/store/`. `query` is the query string without its
 * `?`, and `body` the request's text, or null when it has none. `headers`
 * carries the request's own fields, the sale key (`Idempotency-Key`) and the
 * menu's `If-None-Match` among them; the store reads the ones it knows and
 * keeps the cashier's cookie itself, so none is sent from here.
 */
export interface StoreRequest {
	method: string;
	path: string;
	query: string;
	body: string | null;
	headers: Record<string, string>;
}

/** The store's answer, as the shell hands it back. */
export interface StoreAnswer {
	status: number;
	headers: Record<string, string>;
	body: string;
}

/** Whether this page runs inside the Bosla app's shell. */
export function inShell(): boolean {
	return isTauri();
}

/**
 * Send one call to the store through the shell.
 *
 * It throws when the call has no answer, as a fetch that never reached a
 * server does, so every caller reads it the way it reads a lost connection.
 */
export async function callStore(request: StoreRequest): Promise<StoreAnswer> {
	const answer: unknown = await invoke(STORE_COMMAND, { ...request });
	if (!isAnswer(answer)) {
		throw new TypeError('the shell answered the store call in a shape it does not have');
	}
	return answer;
}

function isAnswer(value: unknown): value is StoreAnswer {
	if (typeof value !== 'object' || value === null) return false;
	const answer = value as Record<string, unknown>;
	return (
		typeof answer.status === 'number' &&
		Number.isInteger(answer.status) &&
		answer.status >= 200 &&
		answer.status <= 599 &&
		typeof answer.body === 'string' &&
		typeof answer.headers === 'object' &&
		answer.headers !== null
	);
}

/**
 * The field that names the screen bundle the till runs, on every call to its
 * store (#1015), in lower case as every other field of the call. The store
 * never forwards it to Bosla: it passes on only the fields it lists.
 */
export const SCREEN_BUILD_HEADER = 'bosla-screen-build';

// The bundle's build, or empty when the bundle names no full commit.
const SCREEN_BUILD = isCommit(BUNDLE_COMMIT) ? BUNDLE_COMMIT : '';

// The till's writes on their way to the store: every call but a read, and
// every attempt holdWrite wraps. An install stops the store, so it waits
// while any is (#925, #1111).
let writes = 0;
const writeCountListeners = new Set<(writes: number) => void>();

function countWrites(by: number): void {
	writes += by;
	for (const listener of writeCountListeners) {
		try {
			listener(writes);
		} catch {
			// Nothing to do: the listener keeps its own state.
		}
	}
}

/**
 * Hold the till's writes on their way while `attempt` runs: a write that
 * reads the store before it sends, such as a cash movement's, is on its way
 * from its first call.
 */
export async function holdWrite<T>(attempt: () => Promise<T>): Promise<T> {
	countWrites(1);
	try {
		return await attempt();
	} finally {
		countWrites(-1);
	}
}

/**
 * Be told how many of the till's writes are on their way, at once and after
 * each change. Returns the way to stop listening.
 */
export function onStoreWrites(listener: (writes: number) => void): () => void {
	writeCountListeners.add(listener);
	listener(writes);
	return () => writeCountListeners.delete(listener);
}

/** Statuses whose answer carries no body, which a Response refuses one for. */
const EMPTY = new Set([204, 205, 304]);

/**
 * A fetch that goes to the store through the shell: the generated client's
 * `fetch`, so every caller keeps its client, its bodies and its pending
 * records (E3). The request's origin is ignored; its path and query are what
 * the store serves. Each call names the bundle's build in
 * SCREEN_BUILD_HEADER when the bundle was built from a commit. A call that is
 * no read counts as a write on its way until its answer comes or is lost
 * (onStoreWrites).
 */
export async function storeFetch(request: Request): Promise<Response> {
	if (request.method === 'GET' || request.method === 'HEAD') return relay(request);
	return holdWrite(() => relay(request));
}

async function relay(request: Request): Promise<Response> {
	const url = new URL(request.url);
	const headers: Record<string, string> = {};
	request.headers.forEach((value, name) => {
		headers[name] = value;
	});
	// Outside Vite, or outside a git checkout, the bundle names no build.
	if (SCREEN_BUILD !== '') headers[SCREEN_BUILD_HEADER] = SCREEN_BUILD;
	const text = request.method === 'GET' || request.method === 'HEAD' ? '' : await request.text();
	let answer: StoreAnswer;
	try {
		answer = await callStore({
			method: request.method,
			path: url.pathname,
			query: url.search.replace(/^\?/, ''),
			body: text === '' ? null : text,
			headers,
		});
	} catch (lost) {
		// A write whose answer is lost may still have been taken.
		tellOpenOrderWrite(request.method, url.pathname);
		throw lost;
	}
	tellOpenOrderWrite(request.method, url.pathname);
	return new Response(EMPTY.has(answer.status) ? null : answer.body, {
		status: answer.status,
		headers: answer.headers,
	});
}

/**
 * A call that can change how many open orders the till holds: a Send, and the
 * payment, the void and the lines of one order, a cancellation of a line
 * included (#348, #352, #379). Nothing but this till writes its open orders,
 * so these are the only calls that move the count.
 */
const OPEN_ORDER_WRITE = /^\/(?:api\/till|store)\/orders(?:\/[^/]+\/(?:pay|void|lines)(?:\/.*)?)?$/;

const writeListeners = new Set<() => void>();

/**
 * Be told after each write to the store's orders that was answered, or whose
 * answer was lost: the store may have taken it either way. Returns the way to
 * stop listening.
 */
export function onOpenOrderWrite(listener: () => void): () => void {
	writeListeners.add(listener);
	return () => writeListeners.delete(listener);
}

function tellOpenOrderWrite(method: string, path: string): void {
	if (method === 'GET' || method === 'HEAD' || !OPEN_ORDER_WRITE.test(path)) return;
	for (const listener of writeListeners) {
		// A listener's failure never turns a write the store took into a lost one.
		try {
			listener();
		} catch {
			// Nothing to do: the listener keeps its own state.
		}
	}
}

/** Whether a URL names a till route, which only the store answers. */
export function isTillCall(url: string): boolean {
	return new URL(url).pathname.startsWith('/api/till/');
}

/** The store's status, as the shell's event store://status carries it. */
export type StoreStatus = 'starting' | 'up' | 'down';

/** The event that carries it. */
export const STATUS_EVENT = 'store://status';

/** What the app is: its version, its native level, the bundle it serves, and
 * the store's status now. */
export interface AppInfo {
	version: string;
	nativeLevel: number;
	bundle: { seq: number; commit: string; builtIn: boolean };
	store: StoreStatus;
}

function isStatus(value: unknown): value is StoreStatus {
	return value === 'starting' || value === 'up' || value === 'down';
}

/**
 * Tell the shell this page has rendered its first screen, with the commit it
 * was built from. It ends a screen bundle's trial only when that bundle is
 * this commit; the shell otherwise returns to the last good one within 60
 * seconds.
 */
export async function bundleReady(commit: string): Promise<boolean> {
	return (await invoke('bundle_ready', { commit })) === true;
}

/**
 * The till is at a safe moment (rest.ts): ask the shell to switch to a newer
 * screen bundle it kept. True when it switches, and the page then reloads.
 */
export async function switchAtRest(): Promise<boolean> {
	return (await invoke('switch_at_rest')) === true;
}

/**
 * The lock screen is at rest (rest.ts): ask the shell to install a newer app
 * update the store kept, once it has checked it again (UC-57, native level 9).
 * True when the install started: on Windows the app then closes, and on
 * Android Android's installer holds it.
 */
export async function installAtRest(): Promise<boolean> {
	return (await invoke('install_at_rest')) === true;
}

/** What the app is. */
export async function appInfo(): Promise<AppInfo> {
	const info = (await invoke('app_info')) as AppInfo;
	if (typeof info !== 'object' || info === null || !isStatus(info.store)) {
		throw new TypeError('the shell answered app_info in a shape it does not have');
	}
	return info;
}

/**
 * Follow the store's status: now, and at every change. Returns the stop.
 */
export async function watchStore(on: (status: StoreStatus) => void): Promise<() => void> {
	// An event is newer than the snapshot read beside it: once one arrives,
	// the snapshot's answer, which may have been read before it, is dropped.
	let heard = false;
	const stop = await listen<unknown>(STATUS_EVENT, (event) => {
		if (!isStatus(event.payload)) return;
		heard = true;
		on(event.payload);
	});
	try {
		const now = (await appInfo()).store;
		if (!heard) on(now);
	} catch {
		// The event still says it at the next change.
	}
	return stop;
}

/**
 * The shell's one word when a manager turns on sharing (#667): `done` once
 * Windows allowed it and the rules were added, `declined` when its
 * administrator said no, `failed` for anything else, `busy` while a run
 * already waits, and `unsupported` where there is no firewall step.
 */
export type SharingAnswer = 'done' | 'declined' | 'failed' | 'busy' | 'unsupported';

const SHARING_ANSWERS: readonly string[] = ['done', 'declined', 'failed', 'busy', 'unsupported'];

/**
 * Ask the shell to add the documented sharing firewall rules on Windows,
 * through Windows' own administrator prompt. It sends nothing: the command
 * is fixed in the app. A word the screen does not know, or no answer at all,
 * reads as `failed`.
 */
export async function turnOnSharing(): Promise<SharingAnswer> {
	try {
		const word: unknown = await invoke('turn_on_sharing');
		return typeof word === 'string' && SHARING_ANSWERS.includes(word)
			? (word as SharingAnswer)
			: 'failed';
	} catch {
		return 'failed';
	}
}
